import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@alfred/convex/_generated/api";
import { Skeleton } from "~/components/ui/skeleton";
import { Badge } from "~/components/ui/badge";
import { relativeTime } from "~/lib/utils";
import { KANBAN_META } from "~/lib/kanban";

export const Route = createFileRoute("/tickets/")({
  component: TicketsList,
});

function TicketsList() {
  const tickets = useQuery(api.tickets.list, {});
  return (
    <div className="mx-auto max-w-5xl px-4 py-6 lg:px-6">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">All tickets</h1>
          <p className="text-xs text-muted-foreground">
            Every Jira ticket Alfred has ever pulled in.
          </p>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2">Key</th>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Repo</th>
              <th className="px-4 py-2">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tickets === undefined
              ? Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    <td className="p-4" colSpan={5}>
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))
              : tickets.length === 0
                ? (
                  <tr>
                    <td
                      className="p-6 text-center text-sm text-muted-foreground"
                      colSpan={5}
                    >
                      No tickets yet. Label any Jira issue in your project with
                      <code className="mx-1 rounded bg-muted px-1 font-mono">
                        alfred
                      </code>
                      and click "Sync Jira now".
                    </td>
                  </tr>
                )
                : tickets.map((t) => {
                    const meta =
                      KANBAN_META[t.status as keyof typeof KANBAN_META];
                    return (
                      <tr
                        key={t._id}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-2 font-mono">
                          <Link
                            to="/tickets/$key"
                            params={{ key: t.jiraKey }}
                            className="text-primary hover:underline"
                          >
                            {t.jiraKey}
                          </Link>
                        </td>
                        <td className="px-4 py-2 max-w-md truncate">{t.title}</td>
                        <td className="px-4 py-2">
                          <Badge variant="outline">
                            <span
                              className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${meta?.bar ?? "bg-muted-foreground"}`}
                            />
                            {meta?.label ?? t.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {t.repoFullName ?? "—"}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {relativeTime(t.jiraUpdatedAt)}
                        </td>
                      </tr>
                    );
                  })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
